import { Translation } from "../../hooks/useTranslations";
import { CurrencyString, Entity, Version } from "../../types";
import { Car } from "../cars/types";
import { ClaimType } from "../claimTypes/types";
import { Subsidiary } from "../subsidiaries/types";
import { User } from "../users/types";

export type ClaimStatus = "open" | "in-progress" | "awaiting-documents" | "closed" | "rejected";

export type Claim = Entity & {
  number?: string | null;
  status: ClaimStatus;
  openingDate: string;
  closingDate?: string;
  incidentDate: string;
  car: Car;
  driver?: User;
  type: ClaimType;
  description?: string;
  damageValueGross?: number;
  damageValueNet?: number;
  damageValueCurrency?: CurrencyString;
  settledAmountGross?: number;
  settledAmountNet?: number;
  settledAmountCurrency?: CurrencyString;
  subsidiary: Subsidiary;
};

export type ClaimNoCarDependency = Omit<Claim, "car"> & {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  };
};

export type ClaimVersion = Version & {
  number?: string | null;
  status: ClaimStatus;
  openingDate: string;
  closingDate?: string;
  incidentDate: string;
  car?: Car;
  driver?: User;
  subsidiary?: Subsidiary;
  type?: ClaimType;
  description?: string;
  damageValueGross?: number;
  damageValueNet?: number;
  damageValueCurrency?: CurrencyString;
  settledAmountGross?: number;
  settledAmountNet?: number;
  settledAmountCurrency?: CurrencyString;
};

export type UpsertClaim = {
  carId: string;
  driverId: string;
  subsidiaryId: string;
  number?: string | null;
  status: string;
  openingDate: string;
  closingDate?: string;
  incidentDate: string;
  typeId: string;
  description?: string;
  damageValueGross?: number | null;
  damageValueNet?: number | null;
  damageValueCurrency?: CurrencyString;
  settledAmountGross?: number | null;
  settledAmountNet?: number | null;
  settledAmountCurrency?: CurrencyString;
  currentVersionNumber?: number;
};

export type ClaimStatusType = {
  value: ClaimStatus;
  label: string;
};

export type ClaimStats = {
  totalOpen: number;
  totalInProgress: number;
  totalAwaitingDocuments: number;
};

export const getClaimStateColor = (status?: ClaimStatus): "green" | "yellow" | "orange" | "red" | "blue" => {
  switch (status) {
    case "open":
      return "green";
    case "in-progress":
      return "yellow";
    case "awaiting-documents":
      return "orange";
    case "closed":
      return "blue";
    case "rejected":
      return "red";
    default:
      return "blue";
  }
};

export const getClaimStatuses = (i18n: Translation): ClaimStatusType[] => [
  { value: "awaiting-documents", label: i18n.common.awaitingDocuments },
  { value: "closed", label: i18n.common.closed },
  { value: "in-progress", label: i18n.common.inProgress },
  { value: "open", label: i18n.common.open },
  { value: "rejected", label: i18n.common.rejected },
];
